import React from "react";
import Select from "react-select";
import { TaxRecord, TaxSource } from "models/Tax";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const matchesTaxSource = (source: TaxSource, query: string, locale: string) => {
    const [year, month, day] = source.date.split("-");
    const amounts = [source.total, source.net].flatMap(amount => [
        amount.toFixed(2), amount.toFixed(2).replace(".", ","), amount.toLocaleString(locale, {minimumFractionDigits: 2}),
    ]);
    const text = normalize([source.name, source.invoice_id, source.mollie_id, source.counter_name, source.counter_iban,
        source.counter_iban?.replace(/\s/g, ""), source.date, `${day}-${month}-${year}`, `${day}/${month}/${year}`, ...amounts].join(" "));
    return normalize(query).trim().split(/\s+/).every(term => text.includes(term));
};

export const sourceCounterparty = (source: TaxSource) => [source.counter_name, source.counter_iban].filter(Boolean).join(" · ");
export const linkedLoanMovement = (records: TaxRecord[], sourceID: string) => records.find(record =>
    record.loan?.movements?.some(movement => movement.source_id === sourceID));

interface Props {
    id?: string;
    value: string;
    sources: TaxSource[];
    records?: TaxRecord[];
    required?: boolean;
    isOptionDisabled?: (source: TaxSource) => boolean;
    onClear?: () => void;
    onChange: (source: TaxSource) => void;
}

const TaxSourceSelect: React.FC<Props> = ({id, value, sources, records = [], required = true, isOptionDisabled, onClear, onChange}) => {
    const {t, intlLocale} = useOfficeTranslation();
    const investmentOnly = (source: TaxSource) => records.some(record => record.investment?.expense_ids?.includes(source.id)) &&
        !records.some(record => record.allocation?.source_id === source.id);
    return <Select<TaxSource>
        inputId={id}
        className="tax-source-select"
        classNamePrefix="tax-transaction-select"
        required={required}
        isClearable={Boolean(onClear)}
        isSearchable
        value={sources.find(source => source.id === value) || null}
        options={sources}
        isOptionDisabled={source => source.id !== value && (investmentOnly(source) || Boolean(linkedLoanMovement(records, source.id)) || Boolean(isOptionDisabled?.(source)))}
        getOptionValue={source => source.id}
        getOptionLabel={source => [source.name, sourceCounterparty(source), source.date,
            source.total.toLocaleString(intlLocale, {style: "currency", currency: "EUR"})].filter(Boolean).join(" · ")}
        formatOptionLabel={source => {
            const owner = linkedLoanMovement(records, source.id);
            const investment = records.some(record => record.investment?.expense_ids?.includes(source.id));
            return <span className="tax-source-option">
            <strong>{source.name}</strong>
            {sourceCounterparty(source) && <small>{sourceCounterparty(source)}</small>}
            <small>{source.date} · {source.total.toLocaleString(intlLocale, {style: "currency", currency: "EUR"})}</small>
            {(source.linked || owner) && <small>{t("taxModule.linked")}{investment ? ` · ${t("taxModule.investment")}` : owner ? ` · ${owner.name}` : ""}</small>}
        </span>;}}
        filterOption={({data}, query) => matchesTaxSource(data, query, intlLocale)}
        placeholder={t("taxModule.searchPlaceholder")}
        noOptionsMessage={() => t("taxModule.noSourcesFound")}
        onChange={source => { if (source) onChange(source); else onClear?.(); }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight={260}
        styles={{menuPortal: base => ({...base, zIndex: 9999})}}
    />;
};

export default TaxSourceSelect;
